import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@org/ui';
import {
  Bell,
  Heart,
  MessageSquare,
  UserPlus,
  Award,
  TrendingUp,
  AtSign,
  Pin,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import type { TFunction } from 'i18next';
import {
  getNotificationLink,
  type NotificationType,
} from '../components/notification-presentation';

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
});

const NOTIFICATION_ICONS: Record<NotificationType, typeof Heart> = {
  post_reply: MessageSquare,
  post_comment: MessageSquare,
  post_like: Heart,
  comment_like: Heart,
  mention: AtSign,
  post_pinned: Pin,
  badge_earned: Award,
  level_up: TrendingUp,
  new_follower: UserPlus,
};

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  post_reply: 'text-blue-500 bg-blue-50',
  post_comment: 'text-blue-500 bg-blue-50',
  post_like: 'text-red-500 bg-red-50',
  comment_like: 'text-red-500 bg-red-50',
  mention: 'text-purple-500 bg-purple-50',
  post_pinned: 'text-amber-500 bg-amber-50',
  badge_earned: 'text-yellow-500 bg-yellow-50',
  level_up: 'text-green-500 bg-green-50',
  new_follower: 'text-primary bg-primary/10',
};

function getNotificationMessage(type: NotificationType, data: any, t: TFunction): string {
  switch (type) {
    case 'post_reply':
      return t('notifications.repliedToPost');
    case 'post_comment':
      return t('notifications.commentedOnPost');
    case 'post_like':
      return t('notifications.likedPost');
    case 'comment_like':
      return t('notifications.likedComment');
    case 'mention':
      return t('notifications.mentionedYou');
    case 'post_pinned':
      return t('notifications.postPinned');
    case 'badge_earned':
      return t('notifications.earnedBadge', { badge: data?.badgeName || 'Badge' });
    case 'level_up':
      return t('notifications.reachedLevel', { level: data?.level || '' });
    case 'new_follower':
      return t('notifications.startedFollowing');
    default:
      return t('notifications.newNotification');
  }
}

function NotificationsPage() {
  const { t } = useTranslation('community');
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const limit = 20;

  const { data, isLoading, refetch } = communityTrpc.notifications.list.useQuery({
    limit,
    offset: page * limit,
    unreadOnly: filter === 'unread',
  });

  const markAsRead = communityTrpc.notifications.markAsRead.useMutation({
    onSuccess: () => refetch(),
  });

  const markAllAsRead = communityTrpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => refetch(),
  });

  const notifications = data?.notifications ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / limit);
  const hasUnread = notifications.some((n) => !n.read);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">{t('notifications.title')}</h1>
          <p className="text-muted-foreground">{t('notifications.subtitle')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {hasUnread && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheck className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2" role="group" aria-label={t('notifications.filterBy')}>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          aria-pressed={filter === 'all'}
          onClick={() => {
            setFilter('all');
            setPage(0);
          }}
        >
          {t('notifications.tabAll')}
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'outline'}
          size="sm"
          aria-pressed={filter === 'unread'}
          onClick={() => {
            setFilter('unread');
            setPage(0);
          }}
        >
          {t('notifications.tabUnread')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" aria-hidden="true" />
            {filter === 'unread' ? t('notifications.unreadTitle') : t('notifications.allTitle')}
            <span className="text-sm font-normal text-muted-foreground">
              ({data?.total ?? 0})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Bell className="mx-auto h-12 w-12 opacity-50" aria-hidden="true" />
              <p className="mt-4">
                {filter === 'unread'
                  ? t('notifications.noUnread')
                  : t('notifications.empty')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => {
                const type = notification.type as NotificationType;
                const Icon = NOTIFICATION_ICONS[type] || Bell;
                const colors = NOTIFICATION_COLORS[type] || 'text-muted-foreground bg-muted';
                const [textColor, bgColor] = colors.split(' ');
                const data = notification.payload as any;
                const link = getNotificationLink(type, data);

                return (
                  <div
                    key={notification.notificationId}
                    className={`flex items-start gap-4 rounded-lg border border-border p-4 transition-colors ${
                      !notification.read ? 'border-primary/20 bg-primary/5' : ''
                    }`}
                  >
                    <div className={`rounded-full p-2 ${bgColor}`}>
                      <Icon className={`h-5 w-5 ${textColor}`} aria-hidden="true" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <Link
                        to={link}
                        onClick={() => {
                          if (!notification.read) {
                            markAsRead.mutate({
                              notificationId: notification.notificationId,
                            });
                          }
                        }}
                        className="block hover:underline"
                      >
                        <p className="text-sm">
                          {data?.actorName && (
                            <span className="font-medium">{data.actorName} </span>
                          )}
                          {getNotificationMessage(type, data, t)}
                        </p>
                        {data?.postTitle && (
                          <p className="mt-1 truncate text-sm text-muted-foreground">
                            &ldquo;{data.postTitle}&rdquo;
                          </p>
                        )}
                      </Link>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>

                    {!notification.read && (
                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    )}
                  </div>
                );
              })}

              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    {t('common.back')}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {t('admin.moderation.page', { page: `${page + 1} / ${totalPages}` })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    {t('admin.bans.next')}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
